const MAX_STRING_LENGTH = 300;
const MAX_ARRAY_LENGTH = 20;
const MAX_OBJECT_KEYS = 30;
const MAX_DEPTH = 4;

const INSTALL_GUARD_KEY = '__OTELLO_CONSOLE_REDACTOR_INSTALLED__';
const CONSOLE_METHODS = ['debug', 'error', 'info', 'log', 'warn'];
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const AUTH_TOKEN_PATTERN = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]+/gi;
const KEY_VALUE_SECRET_PATTERN =
  /\b(access_token|refresh_token|api_access_token|token|secret|api_key|client_secret|password)([=:])([^\s&,;]+)/gi;
const URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const SENSITIVE_KEY_PATTERN =
  /(password|passwd|secret|authorization|cookie|set_cookie|access_token|refresh_token|reset_password_token|api_access_token|api_key|client_secret|private_key|otp|raw_payload|payload|body|content|prompt|transcript|headers?|credential|email|uid|filename|file_name|html|raw|response|data|url|uri|link|message)/i;
const SAFE_METADATA_KEY_PATTERN =
  /(_count|_size|_bytes|_present|_id|_ids|_hash|status|status_code|code|type|method|class|name)$/i;
const SENSITIVE_LABEL_PATTERN =
  /(attachment|filename|file name|url|email|token|header|payload|response|response\.data|request|body|content|html|message)/i;
const PAYLOAD_STRING_PATTERN =
  /(<\/?[a-z][\s\S]*>|"?(body|content|html|payload|headers?|authorization|token|email|url|filename|message)"?\s*:|\{[\s\S]*\}|\[[\s\S]*\])/i;

function truncate(value) {
  if (value.length <= MAX_STRING_LENGTH) return value;
  return `${value.slice(0, MAX_STRING_LENGTH)}[truncated]`;
}

function redactUrl(url) {
  try {
    const parsed = new URL(url);
    parsed.search = '';
    parsed.hash = '';
    if (parsed.pathname && parsed.pathname !== '/') {
      parsed.pathname = '/[FILTERED]';
    }
    return parsed.toString();
  } catch {
    return '[URL]';
  }
}

function redactString(value, { filterPayload = false } = {}) {
  if (filterPayload && PAYLOAD_STRING_PATTERN.test(value)) return '[FILTERED]';

  const redacted = value
    .replace(URL_PATTERN, redactUrl)
    .replace(EMAIL_PATTERN, '[EMAIL]')
    .replace(AUTH_TOKEN_PATTERN, '$1 [FILTERED]')
    .replace(KEY_VALUE_SECRET_PATTERN, '$1$2[FILTERED]');

  return truncate(redacted);
}

function objectType(value) {
  return Object.prototype.toString.call(value).slice(8, -1);
}

function summarizeObject(value) {
  if (!value || typeof value !== 'object') return value;

  return {
    redacted: true,
    object_type: objectType(value),
    keys: Object.keys(value).slice(0, MAX_OBJECT_KEYS),
  };
}

function fileMetadata(value) {
  return {
    redacted: true,
    object_type: objectType(value),
    size: Number.isFinite(value.size) ? value.size : undefined,
    type: typeof value.type === 'string' ? redactString(value.type) : undefined,
    name_present: Boolean(value.name),
  };
}

function errorMetadata(error) {
  const response = error.response || {};
  const config = error.config || {};

  return {
    error_class: error.name || error.constructor?.name || 'Error',
    error_message: redactString(String(error.message || '')),
    code: error.code,
    status: error.status || response.status,
    method: config.method,
    request_url_present: Boolean(config.url),
    response_data_present: Boolean(response.data),
    response_keys:
      response.data && typeof response.data === 'object'
        ? Object.keys(response.data).slice(0, MAX_OBJECT_KEYS)
        : undefined,
    request_present: Boolean(error.request),
  };
}

function isErrorLike(value) {
  return (
    value instanceof Error ||
    Boolean(value?.isAxiosError) ||
    (value &&
      typeof value === 'object' &&
      typeof value.message === 'string' &&
      typeof value.stack === 'string')
  );
}

function isFileLike(value) {
  return (
    value &&
    typeof value === 'object' &&
    typeof value.name === 'string' &&
    Number.isFinite(value.size) &&
    typeof value.type === 'string'
  );
}

function isDomNode(value) {
  return (
    value && typeof value === 'object' && typeof value.nodeType === 'number'
  );
}

function shouldFilterKey(key) {
  return (
    SENSITIVE_KEY_PATTERN.test(key) && !SAFE_METADATA_KEY_PATTERN.test(key)
  );
}

function redactValue(value, seen, depth, contextLabel = '') {
  const filterPayload = SENSITIVE_LABEL_PATTERN.test(contextLabel);

  if (value == null) return value;
  if (isErrorLike(value)) return errorMetadata(value);
  if (typeof value === 'string') return redactString(value, { filterPayload });
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'function')
    return `[Function ${value.name || 'anonymous'}]`;
  if (isFileLike(value)) return fileMetadata(value);
  if (isDomNode(value))
    return { object_type: 'DOMNode', node_name: value.nodeName };

  if (typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular]';
  if (depth >= MAX_DEPTH || filterPayload) return summarizeObject(value);

  seen.add(value);

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((entry) => redactValue(entry, seen, depth + 1, contextLabel));
  }

  const output = {};
  Object.keys(value)
    .slice(0, MAX_OBJECT_KEYS)
    .forEach((key) => {
      if (shouldFilterKey(key)) {
        output[key] = '[FILTERED]';
        return;
      }

      output[key] = redactValue(value[key], seen, depth + 1, key);
    });

  return output;
}

function redactConsoleArgs(args) {
  return args.map((arg, index) => {
    const previousArg = args[index - 1];
    const contextLabel = typeof previousArg === 'string' ? previousArg : '';
    return redactValue(arg, new WeakSet(), 0, contextLabel);
  });
}

export function installConsoleRedactor() {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') return;
  if (window[INSTALL_GUARD_KEY]) return;

  window[INSTALL_GUARD_KEY] = true;

  CONSOLE_METHODS.forEach((method) => {
    // eslint-disable-next-line no-console -- This helper intentionally wraps console methods.
    const original = console[method];
    if (typeof original !== 'function') return;

    // eslint-disable-next-line no-console -- This helper intentionally replaces console methods.
    console[method] = function redactedConsoleMethod(...args) {
      // eslint-disable-next-line no-console -- Preserve original console binding.
      original.apply(console, redactConsoleArgs(args));
    };
  });
}
